import { useCallback, useEffect, useState, type ComponentType } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Award,
  Calendar,
  ChevronRight,
  FileText,
  Gift,
  Headphones,
  Heart,
  Users,
  Wallet,
  Bell,
  UserPlus,
} from 'lucide-react';
import { patientPortal } from '@/api/patientPortal';
import PatientPortalPageHero from './PatientPortalPageHero';
import type { PortalAnnouncement } from './PatientPortalAnnouncementTicker';
import { PATIENT_PORTAL_IMAGES } from './patientPortalImagery';
import { usePatientPortalBranding } from './PatientPortalBrandingProvider';
import { careJourneySubtitle } from './patientPortalBranding';

type NavItem = {
  to: string;
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
};

const quickLinks: NavItem[] = [
  { to: '/patient-loyalty/loyalty', label: 'Points', description: 'Balance, tier & rewards', icon: Award },
  { to: '/patient-loyalty/records', label: 'Health records', description: 'Bills, Rx & reports', icon: FileText },
  { to: '/patient-loyalty/booking', label: 'Book OPD', description: 'Schedule a visit', icon: Calendar },
  { to: '/patient-loyalty/wallet', label: 'Wallet', description: 'Redeem & offers', icon: Wallet },
  { to: '/patient-loyalty/family', label: 'Family', description: 'Linked accounts', icon: Users },
  { to: '/patient-loyalty/challenges', label: 'Challenges', description: 'Wellness goals', icon: Heart },
  { to: '/patient-loyalty/notifications', label: 'Inbox', description: 'Updates & alerts', icon: Bell },
  { to: '/patient-loyalty/contact', label: 'Contact us', description: 'Call, email & locations', icon: Headphones },
];

function NavTile({ to, label, description, icon: Icon }: NavItem) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-4 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200/80 transition hover:shadow-md hover:ring-teal-200/80"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-teal-100">
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-slate-900">{label}</span>
        <span className="block text-xs text-slate-500 mt-0.5">{description}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-teal-600 group-hover:translate-x-0.5" />
    </Link>
  );
}

export default function PatientPortalHome() {
  const branding = usePatientPortalBranding();
  const [profile, setProfile] = useState<{ name: string; uhid: string | null } | null>(null);
  const [loyalty, setLoyalty] = useState<{
    pointsBalance: number;
    membership?: { displayName: string };
    referralCode?: string;
    familyPoolingEnabled?: boolean;
  } | null>(null);
  const [notifications, setNotifications] = useState<Array<{ id: string; title: string; body: string }>>([]);
  const [referralCount, setReferralCount] = useState(0);
  const [announcements, setAnnouncements] = useState<PortalAnnouncement[]>([]);

  const location = useLocation();

  const loadAnnouncements = useCallback(() => {
    patientPortal
      .getAnnouncements()
      .then((rows) => setAnnouncements(Array.isArray(rows) ? rows : []))
      .catch(() => setAnnouncements([]));
  }, []);

  useEffect(() => {
    patientPortal.getMe().then(setProfile);
    patientPortal.getLoyalty().then(setLoyalty);
    patientPortal.getNotifications().then(setNotifications);
    patientPortal.getReferrals().then((rows) => setReferralCount(rows?.length ?? 0));
    loadAnnouncements();
  }, [loadAnnouncements]);

  useEffect(() => {
    if (location.pathname === '/patient-loyalty' || location.pathname === '/patient-loyalty/') {
      loadAnnouncements();
    }
  }, [location.pathname, loadAnnouncements]);

  const firstName = profile?.name?.split(' ')[0] ?? 'there';

  return (
    <div className="space-y-8 pb-4">
      <PatientPortalPageHero
        imageKey="home"
        eyebrow="My Health"
        title={`Welcome back, ${firstName}`}
        subtitle={careJourneySubtitle(branding.hospitalName, profile?.uhid)}
        announcements={announcements}
      />

      <section className="relative overflow-hidden rounded-3xl shadow-lg shadow-teal-950/12 ring-1 ring-teal-900/8">
        <div className="flex flex-col sm:flex-row sm:min-h-[220px] sm:items-stretch bg-gradient-to-br from-teal-950 via-teal-900 to-emerald-950">
          <div className="relative z-10 order-2 sm:order-1 flex-1 px-6 py-7 sm:px-8 sm:py-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between sm:max-w-md">
            <div>
              {loyalty?.membership && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.07] px-3 py-1 text-[11px] font-medium tracking-wide text-teal-100/75 mb-4">
                  <Gift className="h-3.5 w-3.5 text-teal-200/60" />
                  {loyalty.membership.displayName} member
                </span>
              )}
              <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-teal-200/50">
                Available points
              </p>
              <p className="mt-2 text-5xl sm:text-[3.25rem] font-semibold tracking-tight tabular-nums text-white/[0.9]">
                {loyalty?.pointsBalance ?? 0}
              </p>
              {loyalty?.familyPoolingEnabled && (
                <p className="mt-2 text-xs text-teal-100/65">Includes family pool balance</p>
              )}
            </div>

            {loyalty?.referralCode && (
              <div className="sm:text-right sm:pl-8 sm:border-l sm:border-white/[0.08]">
                <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-teal-200/50">
                  Share & earn
                </p>
                <p className="mt-2 font-mono text-lg font-medium tracking-wide text-white/[0.85]">
                  {loyalty.referralCode}
                </p>
              </div>
            )}
          </div>

          <Link
            to="/patient-loyalty/referrals"
            className="mt-6 flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3.5 backdrop-blur-sm transition hover:bg-white/10 sm:max-w-md"
          >
            <span className="flex items-center gap-3 min-w-0">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                <UserPlus className="h-4 w-4 text-teal-200/55" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-white/[0.82]">Refer a friend</span>
                <span className="block text-xs text-teal-200/45 mt-0.5 truncate">
                  {referralCount > 0
                    ? `${referralCount} referral${referralCount === 1 ? '' : 's'} submitted`
                    : 'Send a lead to our care team'}
                </span>
              </span>
            </span>
            <ChevronRight className="h-5 w-5 shrink-0 text-teal-200/50" />
          </Link>
          </div>

          <div className="relative order-1 sm:order-2 h-52 sm:h-full sm:min-h-[220px] sm:w-[42%] sm:min-w-[220px] sm:max-w-[300px] shrink-0 overflow-hidden">
            <img
              src={PATIENT_PORTAL_IMAGES.loyalty}
              alt=""
              role="presentation"
              className="h-full w-full object-cover object-center"
              loading="eager"
              decoding="async"
            />
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-teal-950/90 via-teal-950/25 to-transparent sm:bg-gradient-to-l sm:from-teal-950 sm:via-teal-900/60 sm:to-transparent"
              aria-hidden
            />
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-slate-800 mb-3">Quick access</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {quickLinks.map((item) => (
            <NavTile key={item.to} {...item} />
          ))}
        </div>
      </section>

      {notifications.length > 0 && (
        <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/80">
          <h2 className="text-sm font-semibold text-slate-800 mb-3">Recent updates</h2>
          <ul className="space-y-3">
            {notifications.slice(0, 4).map((n) => (
              <li
                key={n.id}
                className="flex gap-3 text-sm border-b border-slate-100 last:border-0 pb-3 last:pb-0"
              >
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600/70" />
                <span>
                  <span className="font-medium text-slate-800">{n.title}</span>
                  <span className="text-slate-500"> — {n.body}</span>
                </span>
              </li>
            ))}
          </ul>
          <Link
            to="/patient-loyalty/notifications"
            className="inline-flex items-center gap-1 text-sm text-teal-800/80 font-medium mt-4 hover:underline"
          >
            View all <ChevronRight className="h-4 w-4" />
          </Link>
        </section>
      )}
    </div>
  );
}
